/**
 * Regulile pure ale ecranului „Înscriere" (item 1, cererile ECOTIC din 21.09): ce urmeaza dupa
 * `POST /api/auth/otp/inscriere`, si ce se afiseaza la o eroare.
 *
 * Pur, ca `otp-cod.ts`: `vitest.config.ts` ruleaza pe `environment: 'node'`, deci decizia se
 * testeaza aici, nu pe componenta montata.
 *
 * 🔴 Ruta e un oracol de existenta a contului, DELIBERAT (decizia owner-ului) — spre deosebire de
 * `/otp/request`, care raspunde uniform. Deci ecranul chiar poate spune „nu exista cont cu adresa
 * asta"; nu e o scapare. Motivul si limitele:
 * `backend/docs/knowledge/otp-inscriere-oracol-deliberat-2026-09-21.md`.
 */

import { OtpInscriereEsecMotiv } from '../../../api/generated/types.gen';
import { MESAJ_TRIMITERE_ESUATA } from './otp-mesaje';

/** Ce face ecranul dupa raspuns. */
export type PasDupaInscriere =
    /** Contul exista si codul de intrare a plecat: ecranul de introducere a codului. */
    | 'verificare'
    /** Contul exista, dar e de ELEV (nu pleaca niciun cod): inapoi la autentificare. */
    | 'autentificare-elev'
    /** Niciun cont pe adresa: panoul cu «Înregistrează școala nouă» / «Încearcă altă adresă». */
    | 'fara-cont';

/**
 * Raspunsul se citeste defensiv: tipul generat spune `boolean`, dar transformerele nu ruleaza si
 * spec-ul poate minti (`frontend/CLAUDE.md`, pct. 1), deci orice altceva decat `true` e „nu".
 *
 * `codTrimis` se verifica PRIMUL: daca a plecat un cod, singurul ecran pe care il poate folosi
 * cineva e cel de verificare — indiferent ce spune celalalt camp.
 */
export function pasDupaInscriere(raspuns: { areCont?: unknown; codTrimis?: unknown } | null | undefined): PasDupaInscriere {
    if (raspuns?.codTrimis === true) return 'verificare';
    if (raspuns?.areCont === true) return 'autentificare-elev';
    return 'fara-cont';
}

/** Mesajul de pe ecranul de autentificare cand elevul ajunge acolo de pe «Înscriere». */
export const MESAJ_CONT_ELEV = 'Adresa aceasta are deja un cont de elev. Intră în cont de aici.';

/** Unde se afiseaza o eroare: pe camp (langa ce trebuie corectat) sau ca alerta generala. */
export interface EroareInscriere {
    camp: string | null;
    general: string | null;
}

/**
 * Eroarea de afisat, din codul HTTP si din `errors.email` (daca serverul l-a trimis).
 *
 * - 400 cu `errors.email` → pe camp, cu textul serverului (validarea e in serviciu, nu `@Valid`);
 * - 429 `LIMITA_COD` NU ajunge aici: nu e o eroare de afisat, ci un drum (`esteLimitaDeCoduri`);
 * - 503 `TRIMITERE_ESUATA` → contul exista, dar codul n-a putut pleca: se spune exact asta, pe
 *   loc, cu „incearca peste un minut" — niciodata ecranul de cod, care ar pretinde ca a plecat ceva;
 * - 429 (altfel) → plafonul per IP (20 / ora). Mesajul e al nostru: al serverului spune „de pe aceasta
 *   retea", adica vorbeste despre IP, pe care omul nu-l vede. Si NU promite „peste putin":
 *   serverul raspunde cu `Retry-After: 3600`, deci asteptarea poate fi de o ora — iese pe
 *   „mai tarziu" si pe calea care nu trece prin plafon, «Autentificare»;
 * - 404 → ruta nu exista (`auth.otp.enabled` oprit). Mesaj generic, fara sa spunem de ce;
 * - orice altceva → `mesajRezerva`, adica mesajul deja romanesc al erorii (`handleResponse` il
 *   inlocuieste pe cel tehnic), sau textul generic de aici.
 */
export const MESAJ_PREA_MULTE_INCERCARI =
    'Prea multe încercări. Încearcă din nou mai târziu sau intră din «Autentificare».';

export function eroareInscriere(
    status: number | undefined,
    campEmail: string | undefined,
    mesajRezerva: string | undefined,
    motiv?: string
): EroareInscriere {
    if (status === 400 && campEmail && campEmail.trim()) {
        return { camp: campEmail.trim(), general: null };
    }
    if (motiv === OtpInscriereEsecMotiv.TRIMITERE_ESUATA) {
        return { camp: null, general: MESAJ_TRIMITERE_ESUATA };
    }
    if (status === 429) {
        return { camp: null, general: MESAJ_PREA_MULTE_INCERCARI };
    }
    if (status === 404) {
        return { camp: null, general: 'Nu am putut verifica adresa acum. Încearcă din nou mai târziu.' };
    }
    const rezerva = mesajRezerva && mesajRezerva.trim() ? mesajRezerva.trim() : null;
    return { camp: null, general: rezerva ?? 'Nu am putut verifica adresa. Încearcă din nou.' };
}

/**
 * 429 cu `motiv: LIMITA_COD`: contul exista si si-a primit deja numarul maxim de coduri. Serverul
 * NU a mai trimis unul, dar cele primite raman valabile — deci omul trebuie dus pe ecranul de
 * introducere a codului (cu mesajul din `mesajLimitaCod`), nu oprit cu o eroare: are deja in
 * mesaje un cod care merge.
 *
 * Se cer AMANDOUA: un 429 fara motiv (sau cu `LIMITA_IP`) e plafonul per IP si ramane eroare.
 */
export function esteLimitaDeCoduri(status: number | undefined, motiv: string | undefined): boolean {
    return status === 429 && motiv === OtpInscriereEsecMotiv.LIMITA_COD;
}
